import React, { useState } from "react"
import PropTypes from "prop-types"
import { Button, Modal, Space } from "antd"
import { CheckOutlined } from "@ant-design/icons"
import { useAppContext } from "@components/AppContext"
import { useOutletContext } from "react-router-dom"

import { Markdown, MarkdownAlert } from "@components/Text"
import { updateMyInvestmentFundingStatusOperation } from "@api/services/investments"

const LABEL_YES = "Yes, I have sent the funds"
const LABEL_TITLE = "Confirm Fund Transfer"
const LABEL_FUND_SENT = "I have sent funds"
const LABEL_SUBMIT_WIRE_MODAL = "My custodian has confirmed that investment funds are sent"

const LABEL_CONFIRM_ALERT = `Please note, clicking this button does not initiate a
bank debit or wire transfer.`

const LABEL_CONFIRM = `You must complete the wire transfer manually through
your bank using the instructions provided.

Only click "**Yes, I have sent the funds**" after you have completed the transfer.

Once confirmed, your funding status will be updated to Processing. You will be
notified when the funds are received.`

const InvestmentFundedButton = ({
  isSdira,
  afterUpdate,
  hasSentFunds,
  investmentId,
}) => {
  const [ isLoading, setIsLoading ] = useState(false)
  const [ isModalOpen, setIsModalOpen ] = useState(false)

  const { updateInvestment } = useOutletContext()
  const { request } = useAppContext()

  const submitLabel = isSdira
    ? LABEL_SUBMIT_WIRE_MODAL
    : LABEL_FUND_SENT

  const openModal = () =>
    setIsModalOpen(true)

  const onCancel = () =>
    setIsModalOpen(false)

  const confirmFundsSent = () => {
    const parameters = {
      id: investmentId,
      mutation: { hasSentFunds: true }
    }

    setIsLoading(true)
    request(updateMyInvestmentFundingStatusOperation, parameters)
      .then(({ data }) => updateInvestment(data))
      .then(() => afterUpdate())
      .finally(() => setIsLoading(false))
  }

  const onClick = isSdira
    ? confirmFundsSent
    : openModal

  const buttonProps = {
    type: "primary",
    icon: <CheckOutlined />,
    shape: "round",
    loading: isLoading,
    disabled: hasSentFunds,
    onClick,
  }

  const modalProps = {
    open: isModalOpen,
    onOk: confirmFundsSent,
    title: LABEL_TITLE,
    okText: LABEL_YES,
    confirmLoading: isLoading,
    onCancel,
  }

  return (
    <>
      <Button {...buttonProps}>
        {submitLabel}
      </Button>

      <Modal {...modalProps}>
        <Space direction="vertical" size={0}>
          <MarkdownAlert
            text={LABEL_CONFIRM_ALERT}
            type="warning"
            style={{ marginBottom: 0 }}
          />

          <Markdown
            text={LABEL_CONFIRM}
          />
        </Space>
      </Modal>
    </>
  )
}

InvestmentFundedButton.propTypes = {
  isSdira: PropTypes.bool.isRequired,
  afterUpdate: PropTypes.func.isRequired,
  hasSentFunds: PropTypes.bool.isRequired,
  investmentId: PropTypes.string.isRequired,
}

export default InvestmentFundedButton
